/**
 * Main cache store using Zustand with IndexedDB persistence
 * Provides secure, multi-tenant caching with automatic cleanup
 */

import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { UserContext, CacheDataType } from './cacheKeys';
import { CacheEntry, TTL_CONFIG, STORAGE_CONFIG, CacheStats } from './cacheConfig';
import { createStorageAdapter, StorageAdapter } from '@/utils/cacheStorageAdapter';
import { CacheSecurityValidator, withSecurity } from '@/utils/cacheSecurityUtils';

interface CacheState {
  // Core state
  isInitialized: boolean;
  userContext: UserContext | null;
  storageAdapter: StorageAdapter | null;
  
  // Cache statistics
  stats: CacheStats;
  
  // Cache operations
  init: (userContext: UserContext) => Promise<void>;
  get: <T>(key: string) => Promise<T | null>;
  set: <T>(key: string, value: T, ttl?: number) => Promise<void>;
  delete: (key: string) => Promise<void>;
  clear: (pattern?: string) => Promise<void>;
  invalidate: (dataTypes: CacheDataType[], branchId?: string) => Promise<void>;
  
  // Utility methods
  getStats: () => Promise<CacheStats>;
  cleanup: () => Promise<void>;
  clearUserContext: () => Promise<void>;
}

/**
 * Create cache store with security and persistence
 */
export const useCacheStore = create<CacheState>()(
  subscribeWithSelector((set, get) => ({
    // Initial state
    isInitialized: false,
    userContext: null,
    storageAdapter: null,
    stats: {
      totalEntries: 0,
      totalSize: 0,
      hitRate: 0,
      missRate: 0,
      dataTypeBreakdown: {} as any
    },

    /**
     * Initialize cache with user context and storage
     */
    init: async (userContext: UserContext) => {
      try {
        const storageAdapter = await createStorageAdapter();
        
        // Generate cache version for security
        const cacheVersion = CacheSecurityValidator.generateCacheVersion(userContext);
        
        set({
          isInitialized: true,
          userContext,
          storageAdapter
        });

        // Schedule periodic cleanup
        setInterval(() => {
          get().cleanup();
        }, STORAGE_CONFIG.cleanupInterval);

        console.log('Cache store initialized for user:', userContext.userId);
      } catch (error) {
        console.error('Failed to initialize cache store:', error);
        throw error;
      }
    },

    /**
     * Get cached value with security validation
     */
    get: async <T>(key: string): Promise<T | null> => {
      const { storageAdapter, userContext, isInitialized } = get();
      
      if (!isInitialized || !storageAdapter || !userContext) {
        return null;
      }

      // Validate access
      if (!CacheSecurityValidator.validateAccess(key, userContext)) {
        console.warn('Cache access denied for key:', key);
        return null;
      }

      try {
        const entry = await storageAdapter.get(key);
        
        if (!entry) {
          return null;
        }

        // Validate cache data
        if (!CacheSecurityValidator.validateCacheData(entry, userContext)) {
          console.warn('Cache data validation failed for key:', key);
          await storageAdapter.delete(key);
          return null;
        }

        // Check if entry is expired
        if (entry.expiry <= Date.now()) {
          await storageAdapter.delete(key);
          return null;
        }

        return entry.value as T;
      } catch (error) {
        console.error('Cache get error:', error);
        return null;
      }
    },

    /**
     * Set cached value with security and TTL
     */
    set: async <T>(key: string, value: T, ttl?: number): Promise<void> => {
      const { storageAdapter, userContext, isInitialized } = get();
      
      if (!isInitialized || !storageAdapter || !userContext) {
        return;
      }

      // Validate access
      if (!CacheSecurityValidator.validateAccess(key, userContext)) {
        throw new Error('Cache access denied');
      }

      const now = Date.now();
      const effectiveTtl = ttl || TTL_CONFIG.orders; // Default TTL
      
      const entry: CacheEntry<T> = {
        key,
        value,
        timestamp: now,
        expiry: now + effectiveTtl,
        version: CacheSecurityValidator.generateCacheVersion(userContext),
        size: 0, // Will be calculated in storage adapter
        accessCount: 0,
        lastAccessed: now
      };

      try {
        await storageAdapter.set(key, entry);
      } catch (error) {
        console.error('Cache set error:', error);
        throw error;
      }
    },

    /**
     * Delete cached entry
     */
    delete: async (key: string): Promise<void> => {
      const { storageAdapter, userContext, isInitialized } = get();
      
      if (!isInitialized || !storageAdapter || !userContext) {
        return;
      }

      // Validate access
      if (!CacheSecurityValidator.validateAccess(key, userContext)) {
        throw new Error('Cache access denied');
      }

      try {
        await storageAdapter.delete(key);
      } catch (error) {
        console.error('Cache delete error:', error);
        throw error;
      }
    },

    /**
     * Clear cache entries by pattern
     */
    clear: async (pattern?: string): Promise<void> => {
      const { storageAdapter, userContext, isInitialized } = get();
      
      if (!isInitialized || !storageAdapter) {
        return;
      }

      try {
        if (pattern && userContext) {
          // Validate that pattern matches user context
          const securePattern = pattern.includes(userContext.userId) 
            ? pattern 
            : `${userContext.userId}:${pattern}`;
          
          await storageAdapter.clear(securePattern);
        } else {
          // Clear all - should only be used on logout
          await storageAdapter.clear();
        }
      } catch (error) {
        console.error('Cache clear error:', error);
        throw error;
      }
    },

    /**
     * Invalidate specific data types
     */
    invalidate: async (dataTypes: CacheDataType[], branchId?: string): Promise<void> => {
      const { storageAdapter, userContext } = get();
      
      if (!storageAdapter || !userContext) {
        return;
      }

      try {
        const allKeys = await storageAdapter.getAllKeys();
        
        for (const dataType of dataTypes) {
          const pattern = branchId 
            ? `${userContext.userId}:${userContext.restaurantId}:${dataType}:branchId:${branchId}`
            : `${userContext.userId}:${userContext.restaurantId}:${dataType}`;
          
          const keysToDelete = allKeys.filter(key => key.includes(pattern));
          
          await Promise.all(
            keysToDelete.map(key => storageAdapter.delete(key))
          );
        }

        console.log('Cache invalidated for data types:', dataTypes);
      } catch (error) {
        console.error('Cache invalidation error:', error);
      }
    },

    /**
     * Get cache statistics
     */
    getStats: async (): Promise<CacheStats> => {
      const { storageAdapter } = get();
      
      if (!storageAdapter) {
        return get().stats;
      }

      try {
        const allKeys = await storageAdapter.getAllKeys();
        const totalSize = await storageAdapter.getSize();
        
        // Calculate data type breakdown
        const dataTypeBreakdown: Record<CacheDataType, any> = {} as any;
        
        for (const key of allKeys) {
          try {
            const entry = await storageAdapter.get(key);
            if (entry) {
              const keyInfo = key.split(':');
              const dataType = keyInfo[4] as CacheDataType;
              
              if (!dataTypeBreakdown[dataType]) {
                dataTypeBreakdown[dataType] = {
                  count: 0,
                  size: 0,
                  avgAge: 0
                };
              }
              
              dataTypeBreakdown[dataType].count++;
              dataTypeBreakdown[dataType].size += entry.size || 0;
              dataTypeBreakdown[dataType].avgAge += Date.now() - entry.timestamp;
            }
          } catch {
            // Skip invalid entries
          }
        }

        // Calculate average ages
        Object.values(dataTypeBreakdown).forEach((breakdown: any) => {
          if (breakdown.count > 0) {
            breakdown.avgAge = breakdown.avgAge / breakdown.count;
          }
        });

        const stats: CacheStats = {
          totalEntries: allKeys.length,
          totalSize,
          hitRate: 0, // Would need hit/miss tracking for accurate calculation
          missRate: 0,
          dataTypeBreakdown
        };

        set({ stats });
        return stats;
      } catch (error) {
        console.error('Error calculating cache stats:', error);
        return get().stats;
      }
    },

    /**
     * Cleanup expired entries
     */
    cleanup: async (): Promise<void> => {
      const { storageAdapter } = get();
      
      if (!storageAdapter) {
        return;
      }

      try {
        await storageAdapter.cleanup();
      } catch (error) {
        console.error('Cache cleanup error:', error);
      }
    },

    /**
     * Clear user context and all cache data
     */
    clearUserContext: async (): Promise<void> => {
      try {
        await get().clear();
        
        set({
          isInitialized: false,
          userContext: null,
          storageAdapter: null,
          stats: {
            totalEntries: 0,
            totalSize: 0,
            hitRate: 0,
            missRate: 0,
            dataTypeBreakdown: {} as any
          }
        });
      } catch (error) {
        console.error('Error clearing user context:', error);
      }
    }
  }))
);

/**
 * Cache store initialization hook
 */
export const useCacheInitialization = () => {
  const { init, isInitialized } = useCacheStore();
  
  return {
    initCache: init,
    isInitialized
  };
};

/**
 * Cache operations with security wrapper
 */
export const useCacheOperations = () => {
  const store = useCacheStore();
  
  if (!store.userContext) {
    throw new Error('Cache not initialized - missing user context');
  }

  return {
    get: withSecurity(store.userContext, 'read', store.get),
    set: withSecurity(store.userContext, 'write', store.set),
    delete: withSecurity(store.userContext, 'delete', store.delete),
    clear: withSecurity(store.userContext, 'clear', store.clear),
    invalidate: store.invalidate,
    getStats: store.getStats,
    cleanup: store.cleanup
  };
};